// IE Hub - Checkbox Group Component
// Enhanced checkbox group with title and description support

import React, { forwardRef, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Typography,
  alpha,
  styled
} from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ConditionalContainer from '../layout/ConditionalContainer';

interface ValidationRules {
  required?: boolean;
  requiredMessage?: string;
  minSelections?: number;
  maxSelections?: number;
}

interface CheckboxOption {
  value: string | number;
  title: string;
  description?: string;
  disabled?: boolean;
  // Optional rich HTML help shown in a popover via the info icon
  infoHtml?: string;
}

interface CheckboxGroupProps {
  label?: string;
  value?: Array<string | number>;
  onChange?: (value: Array<string | number>, event?: React.ChangeEvent<HTMLInputElement>) => void;
  options: Array<CheckboxOption | string>;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'default';
  row?: boolean;
  variant?: 'standard' | 'outlined' | 'soft';
  sx?: Record<string, unknown>;
  name?: string;
  // ConditionalContainer integration
  title?: string;
  description?: string;
  containerVariant?: 'paper' | 'outlined' | 'plain';
  showErrorInContainer?: boolean;
  [key: string]: any;
}

// Styled components
const StyledFormControl = styled(FormControl)(() => ({
  width: '100%',
}));

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  padding: theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&.Mui-checked': {
    '& .MuiSvgIcon-root': {
      animation: 'checkboxCheck 0.2s ease-in-out',
      color: isSelected ? theme.palette.common.white : theme.palette.primary.main,
    },
  },
  '& .MuiSvgIcon-root': {
    color: isSelected ? theme.palette.common.white : 'inherit',
  },
  '@keyframes checkboxCheck': {
    '0%': {
      transform: 'scale(0.8)',
    },
    '50%': {
      transform: 'scale(1.1)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
}));

const OptionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasDescription' && prop !== 'selected',
})<{ variant?: string; selected?: boolean; disabled?: boolean; hasDescription?: boolean }>(
  ({ theme, variant, selected, disabled, hasDescription }) => ({
    display: 'flex',
    alignItems: hasDescription ? 'flex-start' : 'center',
    padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 1.5,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease-in-out',
    position: 'relative',

    // Hover border color changes only
    '&:hover:not([data-disabled="true"])': {
      borderColor: `${theme.palette.primary.main} !important`,
    },

    ...(variant === 'outlined' && {
      border: `1px solid ${selected
          ? theme.palette.primary.main
          : disabled
            ? theme.palette.action.disabled
            : theme.palette.divider
        }`,
      backgroundColor: selected
        ? alpha(theme.palette.primary.main, 0.92)
        : theme.palette.background.paper,
      '&:hover': !disabled && {
        borderColor: theme.palette.primary.main,
      },
    }),

    ...(variant === 'soft' && {
      backgroundColor: selected
        ? alpha(theme.palette.primary.main, 0.92)
        : disabled
          ? alpha(theme.palette.action.disabled, 0.05)
          : alpha(theme.palette.background.default, 0.4),
      border: `1px solid ${selected
          ? alpha(theme.palette.primary.main, 0.4)
          : 'transparent'
        }`,
      '&:hover': !disabled && {
        border: `1px solid ${theme.palette.primary.main}`,
      },
    }),

    ...(variant === 'standard' && {
      backgroundColor: 'transparent',
      '&:hover': !disabled && {
        borderColor: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
      },
    }),

    ...(disabled && {
      opacity: 0.6,
      '&:hover': {
        borderColor: theme.palette.action.disabled,
      },
    }),
  })
);

const ContentContainer = styled(Box)(() => ({
  flex: 1,
  marginLeft: 8,
  display: 'flex',
  flexDirection: 'column',
}));

const TitleText = styled(Typography)<{ selected?: boolean; disabled?: boolean }>(
  ({ theme, selected, disabled }) => ({
    fontWeight: selected ? 600 : 500,
    color: disabled
      ? theme.palette.text.disabled
      : selected
        ? theme.palette.common.white
        : theme.palette.text.primary,
    lineHeight: 1.4,
    marginBottom: 0,
    transition: 'color 0.2s ease-in-out',
  })
);

const DescriptionText = styled(Typography)<{ disabled?: boolean; selected?: boolean }>(
  ({ theme, disabled, selected }) => ({
    color: disabled
      ? theme.palette.text.disabled
      : selected
        ? alpha(theme.palette.common.white, 0.8)
        : theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    marginTop: theme.spacing(0.5),
    transition: 'color 0.2s ease-in-out',
  })
);

const CheckboxGroup = forwardRef<HTMLInputElement, CheckboxGroupProps>(({
  label,
  value = [],
  onChange,
  options = [],
  validation = {},
  onValidate,
  error = false,
  helperText,
  disabled = false,
  size = 'medium',
  color = 'primary',
  row = false,
  variant = 'outlined',
  sx = {},
  name,
  // ConditionalContainer props
  title,
  description,
  containerVariant = 'plain',
  showErrorInContainer = true
}, _ref) => {
  const [validationError, setValidationError] = useState('');
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const [infoContentHtml, setInfoContentHtml] = useState<string>('');

  // Normalize options
  const normalizedOptions = React.useMemo(() => {
    return options.map((opt: string | CheckboxOption) =>
      typeof opt === 'string'
        ? { value: opt, title: opt }
        : opt
    );
  }, [options]);

  // Open/close handlers for popover
  const handleOpenInfo = (event: React.MouseEvent<HTMLElement>, html?: string) => {
    event.stopPropagation();
    if (!html) return;
    setInfoAnchorEl(event.currentTarget);
    setInfoContentHtml(html);
  };
  const handleCloseInfo = () => {
    setInfoAnchorEl(null);
    setInfoContentHtml('');
  };

  // Validation logic
  const validateInput = (inputValue: Array<string | number>) => {
    let errorMsg = '';

    if (validation.required && (!inputValue || inputValue.length === 0)) {
      errorMsg = validation.requiredMessage || 'Please select at least one option';
    }

    if (validation.minSelections && inputValue.length < validation.minSelections) {
      errorMsg = `Please select at least ${validation.minSelections} options`;
    }

    if (validation.maxSelections && inputValue.length > validation.maxSelections) {
      errorMsg = `Please select at most ${validation.maxSelections} options`;
    }

    setValidationError(errorMsg);

    if (onValidate) {
      onValidate(errorMsg === '', errorMsg);
    }

    return errorMsg === '';
  };

  // Handle checkbox change
  const handleChange = (optionValue: string | number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const currentValues = value || [];
    let newValues = [...currentValues];

    if (checked) {
      newValues.push(optionValue);
    } else {
      newValues = newValues.filter(v => v !== optionValue);
    }

    // Validate
    if (Object.keys(validation).length > 0) {
      validateInput(newValues);
    }

    if (onChange) {
      onChange(newValues, event);
    }
  };

  // Handle option container click
  const handleOptionClick = (optionValue: string | number, optionDisabled?: boolean) => {
    if (disabled || optionDisabled) return;

    const currentValues = value || [];
    const isCurrentlySelected = currentValues.includes(optionValue);

    // Create synthetic event
    const syntheticEvent = {
      target: { checked: !isCurrentlySelected }
    } as React.ChangeEvent<HTMLInputElement>;

    handleChange(optionValue)(syntheticEvent);
  };

  const hasError = !!validationError || error;
  const displayHelperText = validationError || helperText;

  // Custom icon props for checkbox
  const checkboxIconProps = {
    icon: <CheckBoxOutlineBlankIcon />,
    checkedIcon: <CheckBoxIcon />,
    indeterminateIcon: <IndeterminateCheckBoxIcon />,
  };

  // Determine if we should show error in container
  const shouldShowErrorInContainer = showErrorInContainer && hasError && validationError;
  const containerErrorMessage = shouldShowErrorInContainer ? validationError : undefined;

  // Content to render inside ConditionalContainer
  const checkboxContent = (
    <StyledFormControl error={hasError && !shouldShowErrorInContainer} sx={{ width: '100%' }}>
      {label && !title && (
        <Typography
          variant="subtitle1"
          component="legend"
          sx={{
            mb: 2,
            color: hasError ? 'error.main' : 'text.primary',
            fontWeight: 600,
            letterSpacing: '-0.02em'
          }}
        >
          {label}
          {validation.required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
          )}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: row ? 'row' : 'column',
          flexWrap: row ? 'wrap' : 'nowrap',
          gap: variant === 'standard' ? 0.5 : 0,
        }}
      >
        {normalizedOptions.map((option: CheckboxOption) => {
          const isSelected = (value || []).includes(option.value);
          const isDisabled = option.disabled || disabled;

          return (
            <OptionContainer
              key={option.value}
              variant={variant}
              selected={isSelected}
              disabled={isDisabled}
              hasDescription={!!option.description}
              onClick={() => handleOptionClick(option.value, option.disabled)}
              data-disabled={isDisabled}
              sx={{
                ...(row && {
                  minWidth: 'fit-content',
                  marginRight: 2,
                  marginBottom: 1
                })
              }}
            >
              <FormControlLabel
                control={
                  <StyledCheckbox
                    checked={isSelected}
                    onChange={handleChange(option.value)}
                    size={size}
                    color={color}
                    disabled={isDisabled}
                    name={name}
                    isSelected={isSelected}
                    {...checkboxIconProps}
                    sx={{
                      padding: variant === 'standard' ? 1 : 0.75,
                      alignSelf: option.description ? 'flex-start' : 'center',
                    }}
                  />
                }
                label=""
                sx={{
                  margin: 0,
                  alignItems: option.description ? 'flex-start' : 'center',
                }}
              />

              <ContentContainer>
                <TitleText
                  variant="body1"
                  selected={isSelected}
                  disabled={isDisabled}
                  className="option-title"
                >
                  {option.title}
                </TitleText>

                {option.description && (
                  <DescriptionText
                    variant="body2"
                    disabled={isDisabled}
                    selected={isSelected}
                    className="option-description"
                  >
                    {option.description}
                  </DescriptionText>
                )}
              </ContentContainer>

              {typeof option.infoHtml === 'string' && option.infoHtml.trim().length > 0 && (
                <Box
                  sx={{
                    alignSelf: option.description ? 'flex-start' : 'center',
                    ml: 1
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip title="More info">
                    <IconButton
                      size="small"
                      aria-label="More info"
                      onClick={(e) => handleOpenInfo(e, option.infoHtml)}
                      sx={{
                        p: 0.5,
                        color: isSelected ? 'common.white' : 'primary.main',
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}
                    >
                      <InfoOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </OptionContainer>
          );
        })}
      </Box>

      {displayHelperText && !shouldShowErrorInContainer && (
        <FormHelperText error={hasError} sx={{ mt: 1.5, ml: 0 }}>
          {displayHelperText}
        </FormHelperText>
      )}

      <Popover
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={handleCloseInfo}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            maxWidth: 360,
            p: 1.5
          }
        }}
      >
        <Box
          sx={{ maxHeight: 300, overflowY: 'auto', pr: 0.5 }}
          // Content is curated from our own legacy docs; render as HTML
          dangerouslySetInnerHTML={{ __html: infoContentHtml }}
        />
      </Popover>
    </StyledFormControl>
  );

  const containerProps: any = {
    ifError: shouldShowErrorInContainer,
    variant: containerVariant,
    sx,
  };

  if (title || (label && title !== undefined)) {
    containerProps.title = title || label;
  }

  if (description) {
    containerProps.description = description;
  }

  if (containerErrorMessage) {
    containerProps.errorMessage = containerErrorMessage;
  }

  return (
    <ConditionalContainer {...containerProps}>
      {checkboxContent}
    </ConditionalContainer>
  );
});

CheckboxGroup.displayName = 'CheckboxGroup';

export default CheckboxGroup;
